import { useMemo, useState } from 'react'
import { Lock } from 'lucide-react'
import type { NtCourse, NtStudent, NtProposal, NtTerm, NtPlacement, NtPlacementRow, NtOverride } from '../../core/types'
import { runAndSavePlacement, useOverrides, saveHandPlacement, removeHandPlacement } from './model'
import { placementHealth } from './placement'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Build the week — run placement, look at what it did, publish the rosters.
 *
 * Two buttons, not one. Running placement honours what students asked for;
 * balancing then deliberately breaks some of that to save under-enrolled
 * courses from cancellation. That second move costs somebody their second
 * choice, so a person chooses it with the numbers in front of them rather than
 * the machine doing it quietly.
 *
 * Publishing writes one roster per course in the same transaction as the
 * placement, because a roster one run out of date is worse than no roster —
 * the teacher believes it.
 */
export function BuildWeek({ term, courses, students, proposals, placement, who }: {
  term: NtTerm
  courses: NtCourse[]
  students: NtStudent[] | null
  proposals: NtProposal[]
  placement: NtPlacement | null
  who: string
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const overrides = useOverrides(term.id, true)

  const ready = courses.filter((c) => c.status === 'approved' && c.block)
  const health = placement ? placementHealth(placement, courses) : null
  const byId = new Map(courses.map((c) => [c.id, c]))

  const go = (balance: boolean) => async () => {
    if (!students?.length) { setErr('Upload the student list first.'); return }
    setBusy(true); setErr(null); setNote(null)
    try {
      const r = await runAndSavePlacement(
        term.id, courses, students, proposals, term.rules.placementPriority, who, balance, overrides,
      )
      setNote(balance
        ? `Placed ${r.placement.rows.length} students, moved ${r.moved} to fill under-enrolled courses, published ${r.rosters} rosters.`
        : `Placed ${r.placement.rows.length} students and published ${r.rosters} rosters.`)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  if (ready.length === 0) {
    return <Empty title="Not ready to place" body="Courses have to be approved and assigned a block before placement can run." />
  }

  return (
    <>
      <div className="card" style={{ marginTop: 'var(--s-2)' }}>
        <div className="dl">
          <div><div className="dl__k">Courses ready</div><div className="dl__v">{ready.length}</div></div>
          <div><div className="dl__k">Students</div><div className="dl__v">{students?.length ?? 0}</div></div>
          <div><div className="dl__k">Projects approved</div>
            <div className="dl__v">{proposals.filter((p) => p.status === 'approved').length}</div></div>
          <div><div className="dl__k">Priority</div><div className="dl__v">{term.rules.placementPriority}</div></div>
        </div>
      </div>

      {err && <ErrorState body={err} />}
      {note && (
        <div className="notice notice--info" style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">✓</span><div>{note}</div>
        </div>
      )}

      <div className="opt__bar">
        <button className="btn" onClick={go(false)} disabled={busy}>
          {busy ? 'Running…' : placement ? 'Run again' : 'Run placement'}
        </button>
        <button className="btn btn--ghost" onClick={go(true)} disabled={busy}>
          Run and balance
        </button>
      </div>
      <p className="opt__hint">
        Balancing moves students into courses that are below their minimum, preferring people who
        actually ranked them. It saves the four-person class at the cost of somebody's second choice.
      </p>

      {placement && health && (
        <>
          <h2 className="work__heading">Result</h2>
          <div className="card">
            <div className="dl">
              <div><div className="dl__k">First choice</div><div className="dl__v">{health.firstChoicePct}%</div></div>
              <div><div className="dl__k">Unplaced</div><div className="dl__v">{health.unplaced}</div></div>
              <div><div className="dl__k">Under minimum</div><div className="dl__v">{health.under.length}</div></div>
              <div><div className="dl__k">Full</div><div className="dl__v">{health.full.length}</div></div>
            </div>
          </div>

          {health.under.length > 0 && (
            <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
              <span aria-hidden="true">⚠</span>
              <div>
                <strong>Below minimum (these usually get cancelled):</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {health.under.map((c) => (
                    <li key={c.id}>{c.title}: {placement.seats[c.id] ?? 0} of {c.min} needed</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <h2 className="work__heading">Seats by course</h2>
          <ul className="work">
            {ready.map((c) => {
              const n = placement.seats[c.id] ?? 0
              const short = n < c.min
              return (
                <li key={c.id} className={`work__row${short ? ' is-live' : ''}`}>
                  <div className="work__main">
                    <div style={{ fontWeight: 610 }}>{c.title}</div>
                    <div className="work__sub">{c.block} · {c.teacherName}</div>
                  </div>
                  <span className={`pill ${short ? 'pill--serious' : n >= c.cap ? 'pill--good' : ''}`}>
                    {n} / {c.cap}
                  </span>
                </li>
              )
            })}
          </ul>

          <HandPlace term={term} courses={ready} students={students ?? []}
            placement={placement} overrides={overrides} who={who} />

          <h2 className="work__heading">Every student</h2>
          <ul className="work">
            {placement.rows.slice(0, 80).map((r) => (
              <li key={r.studentId} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>
                    {r.name} <span className="cat__meta">gr {r.grade}</span>
                    {(r.amLocked || r.pmLocked) && (
                      <span className="pill" style={{ marginLeft: 6 }}><Lock size={11} aria-hidden="true" /> placed by hand</span>
                    )}
                  </div>
                  <div className="work__sub">
                    {r.independent
                      ? `Independent: ${r.independentTitle}`
                      : `AM ${byId.get(r.amCourseId ?? '')?.title ?? 'unplaced'}${r.amChoice ? ` (#${r.amChoice})` : ''}
                         · PM ${byId.get(r.pmCourseId ?? '')?.title ?? 'unplaced'}${r.pmChoice ? ` (#${r.pmChoice})` : ''}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {placement.rows.length > 80 && (
            <p className="opt__hint">Showing the first 80 of {placement.rows.length}.</p>
          )}
        </>
      )}
    </>
  )
}

/**
 * Putting a student in a course on purpose — a schedule conflict, an IEP, a
 * kid who needs to be with a particular teacher. Takes effect on the rosters
 * now, and every later run keeps them there; balancing never moves them.
 */
function HandPlace({ term, courses, students, placement, overrides, who }: {
  term: NtTerm
  courses: NtCourse[]
  students: NtStudent[]
  placement: NtPlacement
  overrides: NtOverride[]
  who: string
}) {
  const [q, setQ] = useState('')
  const [pick, setPick] = useState<NtPlacementRow | null>(null)
  const [am, setAm] = useState('')
  const [pm, setPm] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const byId = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses])
  const nameOf = (sid: string) => placement.rows.find((r) => r.studentId === sid)?.name ?? sid
  const matches = q.trim().length < 2 ? [] : placement.rows
    .filter((r) => !r.independent && r.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)

  const choose = (r: NtPlacementRow) => { setPick(r); setAm(''); setPm(''); setWhy(''); setQ('') }
  const option = (c: NtCourse) => `${c.title} (${placement.seats[c.id] ?? 0}/${c.cap}${(placement.seats[c.id] ?? 0) >= c.cap ? ', full' : ''})`

  const save = async () => {
    if (!pick || (!am && !pm)) return
    setBusy(true); setErr(null)
    try {
      // One record per student: keep the block not being changed (a credit
      // recovery morning must survive placing their afternoon by hand).
      const had = overrides.find((o) => o.studentId === pick.studentId)
      const keepAm = am || had?.amCourseId
      const keepPm = pm || had?.pmCourseId
      await saveHandPlacement(term.id, {
        studentId: pick.studentId, ...(keepAm ? { amCourseId: keepAm } : {}), ...(keepPm ? { pmCourseId: keepPm } : {}),
        ...(why.trim() ? { note: why.trim() } : {}),
      }, placement, courses, students, who)
      setPick(null)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const unlock = (sid: string) => async () => {
    setBusy(true); setErr(null)
    try { await removeHandPlacement(term.id, sid, placement) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  return (
    <>
      <h2 className="work__heading">Place a student by hand</h2>
      <section className="card">
        {err && <ErrorState body={`Not saved. ${err}`} />}
        {!pick ? (
          <>
            <input className="opt__input" placeholder="Find a student by name" value={q}
              aria-label="Find a student by name" onChange={(e) => setQ(e.target.value)} />
            {matches.length > 0 && (
              <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
                {matches.map((r) => (
                  <li key={r.studentId} className="work__row">
                    <button className="row-link" onClick={() => choose(r)}>{r.name}</button>
                    <span className="work__sub" style={{ marginLeft: 'auto' }}>
                      {byId.get(r.amCourseId ?? '')?.title ?? 'no AM'} · {byId.get(r.pmCourseId ?? '')?.title ?? 'no PM'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="opt__hint">
              They go straight onto that course's roster, and every later run keeps them there, even
              past the cap, because you chose it. Balancing never moves them.
            </p>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 var(--s-3)', fontWeight: 610 }}>{pick.name}</p>
            <label className="opt__label" htmlFor="hp-am">Morning</label>
            <select id="hp-am" className="opt__input" value={am} onChange={(e) => setAm(e.target.value)}
              style={{ margin: '4px 0 var(--s-3)' }}>
              <option value="">Keep {byId.get(pick.amCourseId ?? '')?.title ?? 'as placement decides'}</option>
              {courses.filter((c) => c.block === 'AM' && c.id !== pick.amCourseId)
                .map((c) => <option key={c.id} value={c.id}>{option(c)}</option>)}
            </select>
            <label className="opt__label" htmlFor="hp-pm">Afternoon</label>
            <select id="hp-pm" className="opt__input" value={pm} onChange={(e) => setPm(e.target.value)}
              style={{ margin: '4px 0 var(--s-3)' }}>
              <option value="">Keep {byId.get(pick.pmCourseId ?? '')?.title ?? 'as placement decides'}</option>
              {courses.filter((c) => c.block === 'PM' && c.id !== pick.pmCourseId)
                .map((c) => <option key={c.id} value={c.id}>{option(c)}</option>)}
            </select>
            <input className="opt__input" placeholder="Why (optional, only the office sees this)" value={why}
              aria-label="Why" onChange={(e) => setWhy(e.target.value)} />
            <div className="opt__bar">
              <button className="btn" disabled={busy || (!am && !pm)} onClick={save}>
                {busy ? 'Saving…' : 'Place them'}
              </button>
              <button className="btn btn--ghost" disabled={busy} onClick={() => setPick(null)}>Cancel</button>
            </div>
          </>
        )}
      </section>

      {overrides.length > 0 && (
        <>
          <h2 className="work__heading">Placed by hand <span className="work__badge">{overrides.length}</span></h2>
          <ul className="work">
            {overrides.map((o) => (
              <li key={o.id} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{nameOf(o.studentId)}</div>
                  <div className="work__sub">
                    {[o.amCourseId && `AM ${byId.get(o.amCourseId)?.title ?? o.amCourseId}`,
                      o.pmCourseId && `PM ${byId.get(o.pmCourseId)?.title ?? o.pmCourseId}`].filter(Boolean).join(' · ')}
                    {' '}· by {o.by}{o.note ? ` · ${o.note}` : ''}
                  </div>
                </div>
                <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={busy} onClick={unlock(o.studentId)}>
                  Let placement decide
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
